import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  usePatientTreatmentPlans,
  groupPlanItemsByVisit,
  useUpsertPlanItem,
  type EnrichedPlanItem,
} from '@/hooks/useTreatmentPlans';
import { PlanVisitGroup } from './PlanVisitGroup';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';

// ── Props ─────────────────────────────────────────────────────────────────────

interface PlanPanelProps {
  patientId: string;
  clinicId: string;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PlanPanel({ patientId, clinicId, className }: PlanPanelProps) {
  const { t } = useTranslation();
  const [editingItem, setEditingItem] = useState<EnrichedPlanItem | null>(null);

  // Realtime subscription hoisted to DentalModulePage (single channel per mount).

  const { data: planItems = [], isLoading } = usePatientTreatmentPlans(patientId);
  const upsert = useUpsertPlanItem();

  const visitGroups = groupPlanItemsByVisit(planItems);
  const allVisitNumbers = visitGroups.map((g) => g.visit_number);

  const totalEstimate = planItems.reduce((sum, item) => sum + (item.estimated_cost ?? 0), 0);
  const visitCount = visitGroups.length;

  const handleAddVisit = async () => {
    const nextVisit = allVisitNumbers.length > 0 ? Math.max(...allVisitNumbers) + 1 : 1;
    try {
      await upsert.mutateAsync({
        patientId,
        toothFdi: '11',
        treatmentType: 'PLAN',
        treatmentDescription: t('dental.plan.newVisitPlaceholder', {
          defaultValue: 'Nyt besøg — redigér dette element',
        }),
        visitNumber: nextVisit,
      });
      toast.success(
        t('dental.plan.visitAdded', { defaultValue: `Besøg ${nextVisit} oprettet`, n: nextVisit }),
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(msg);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-8', className)}>
        <Loader2 className="h-5 w-5 animate-spin text-ds-teal" />
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ds-line bg-ds-card flex-shrink-0">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-ds-text">
            {t('dental.plan.title')}
          </h3>
          {visitCount > 0 && (
            <span className="text-xs text-ds-muted">
              {t('dental.plan.summary', {
                visits: visitCount,
                total: totalEstimate.toLocaleString('da-DK'),
              })}
            </span>
          )}
        </div>

        <Button type="button" onClick={handleAddVisit} disabled={upsert.isPending}>
          {upsert.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
          {t('dental.plan.addVisit')}
        </Button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-ds-sunk">
        {visitGroups.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-center">
            <p className="text-sm text-ds-muted max-w-xs">
              {t('dental.plan.emptyState')}
            </p>
          </div>
        ) : (
          visitGroups.map((group) => (
            <PlanVisitGroup
              key={group.visit_number}
              group={group}
              patientId={patientId}
              clinicId={clinicId}
              allVisitNumbers={allVisitNumbers}
              onEditRequest={setEditingItem}
            />
          ))
        )}

        {/* Total estimate footer */}
        {totalEstimate > 0 && (
          <div className="flex items-center justify-end px-4 py-2 rounded-lg bg-ds-card border border-ds-line">
            <span className="text-sm text-ds-muted mr-3">
              Total estimat:
            </span>
            <span className="text-base font-bold text-ds-text">
              {totalEstimate.toLocaleString('da-DK')} kr
            </span>
          </div>
        )}
      </div>

      {/* Edit drawer placeholder — QuickRegistrationDrawer is tied to shortcut/procedure type;
          inline editing is handled via the existing drawer from the tooth chart stage.
          editingItem state is maintained here for future wiring. */}
      {editingItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ds-scrim"
          onClick={() => setEditingItem(null)}
        >
          <div
            className="bg-ds-card rounded-xl shadow-pop p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm font-semibold text-ds-text mb-1">
              Tand {editingItem.tooth_fdi} — {editingItem.treatment_type}
            </p>
            <p className="text-xs text-ds-muted">
              {editingItem.procedure_name_da ?? editingItem.treatment_description}
            </p>
            <Button type="button" variant="secondary" size="full" className="mt-4" onClick={() => setEditingItem(null)}>
              Luk
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
